import { createSlice, type PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'

import apiClient from '@/libs/apiClient'
import type { Banner } from '@/types/marketing'

interface Pagination {
  currentPage: number
  lastPage: number
  perPage: number
  total: number
}

interface BannerState {
  banners: Banner[]
  pagination: Pagination | null
  loading: boolean
  error: string | null
}

const initialState: BannerState = {
  banners: [],
  pagination: null,
  loading: false,
  error: null
}

// Fetch all banners
export const fetchBanners = createAsyncThunk<
  { banners: Banner[]; pagination: Pagination },
  { limit?: number } | undefined,
  { rejectValue: string }
>('banners/fetchBanners', async (params = {}, { rejectWithValue }) => {
  try {
    const limit = params.limit || 1000
    const res = await apiClient.get('/admin/v1/banners', { params: { limit } })
    const json = res.data

    const pagination: Pagination = {
      currentPage: json.meta.currentPage,
      lastPage: json.meta.lastPage,
      perPage: json.meta.perPage,
      total: json.meta.total
    }

    return { banners: json.data as Banner[], pagination }
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || err.message || 'Failed to fetch banners')
  }
})

// Add Banner
export const addBanner = createAsyncThunk<Banner, FormData, { rejectValue: string }>(
  'banners/addBanner',
  async (formData, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/admin/v1/banners/add', formData)
      const data = res.data

      return data.data as Banner
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to add banner')
    }
  }
)

// Update Banner
export const updateBanner = createAsyncThunk<Banner, { id: string; formData: FormData }, { rejectValue: string }>(
  'banners/updateBanner',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`/admin/v1/banners/${id}/edit`, formData)
      const data = res.data

      return data.data as Banner
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to update banner')
    }
  }
)

// Toggle Banner Status
export const toggleBanner = createAsyncThunk<Banner, { id: string; is_enabled: boolean }, { rejectValue: string }>(
  'banners/toggleBanner',
  async ({ id, is_enabled }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`/admin/v1/banners/${id}/toggle`, { is_enabled })
      const data = res.data

      return data.data as Banner
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to toggle banner status')
    }
  }
)

// Delete Banner
export const deleteBanner = createAsyncThunk<string, string, { rejectValue: string }>(
  'banners/deleteBanner',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/admin/v1/banners/${id}/delete`)

      return id
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message || 'Failed to delete banner')
    }
  }
)

const bannerSlice = createSlice({
  name: 'banners',
  initialState,
  reducers: {
    clearBanners(state) {
      state.banners = []
      state.pagination = null
      state.error = null
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchBanners.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchBanners.fulfilled, (state, action) => {
        state.loading = false
        state.banners = action.payload.banners
        state.pagination = action.payload.pagination
      })
      .addCase(fetchBanners.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to fetch banners'
      })
      .addCase(addBanner.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(addBanner.fulfilled, (state, action) => {
        state.loading = false
        state.banners.unshift(action.payload)
        if (state.pagination) state.pagination.total += 1
      })
      .addCase(addBanner.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to add banner'
      })
      .addCase(updateBanner.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateBanner.fulfilled, (state, action) => {
        state.loading = false
        state.banners = state.banners.map(b => (b.id === action.payload.id ? action.payload : b))
      })
      .addCase(updateBanner.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to update banner'
      })
      .addCase(toggleBanner.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(toggleBanner.fulfilled, (state, action) => {
        state.loading = false
        state.banners = state.banners.map(b => (b.id === action.payload.id ? action.payload : b))
      })
      .addCase(toggleBanner.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to toggle banner status'
      })
      .addCase(deleteBanner.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteBanner.fulfilled, (state, action) => {
        state.loading = false
        state.banners = state.banners.filter(b => b.id !== action.payload)
        if (state.pagination) state.pagination.total -= 1
      })
      .addCase(deleteBanner.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || 'Failed to delete banner'
      })
  }
})

export const { clearBanners } = bannerSlice.actions
export default bannerSlice.reducer
